import {
  DatePicker,
  Space,
  Tooltip,
  InputNumber,
  Input,
  Radio,
  Checkbox,
  Button,
  Select,
  Switch,
  Form,
  notification
} from 'antd';
import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import ComparisonTableMRI from './ComparisonTableMRI';
import {
  StandardIcon,
  StandingIcon,
  DilapidatedIcon,
  HouseIcon,
  OutdoorPoolIcon,
  SolarPanelIcon,
  OfficeIcon,
  CommercialIcon,
  HotelIcon,
  PoliticalOfficeIcon,
  PlaceWorshipIcon,
  OtherBuildingIcon,
  PersonIcon,
  EntityIcon,
  UnoccupiedIcon,
  ConstructionIcon,
  ConstructionLongIcon
} from '../../utils/constants/formIcons';
import GoogleMapsIcon from '../../assets/images/icons/Google_Maps_icon.png';
import { isValidZipcode } from '../../utils/zipcode';

const { Option } = Select;
const dateFormat = 'DD/MM/YYYY';
const dateFormatYr = 'YYYY';

const useMRIMLFields = (form) => {
  const {
    exemptions,
    formValues,
    setFormValues,
    enums,
    postalCode,
    setPostalCode,
    estimatedCost,
    catNat,
    errorGetCities,
    setErrorGetCities
  } = useQuotesContext();
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [addressSuggestion, setAddressSuggestion] = useState([]);
  const [cities, setCities] = useState([]);
  const number_activity = form.getFieldValue('number_activity');
  const isAmendment = window.location.href.search('amendment');
  const [checkedVal, setCheckedVal] = useState('');
  const [displayCollapse, setDisplayCollapse] = useState(true);

  const acquisitionDate = formValues?.acquisition_date
    ? new Date(formValues.acquisition_date)
    : null;

  const isAcquisitionDateMoreThan2MonthsOld =
    acquisitionDate && moment().diff(acquisitionDate, 'days') > 60;

  useEffect(() => {
    const currentYear = new Date().getFullYear();

    const constructionYear = formValues?.construction_year
      ? new Date(formValues.construction_year).getFullYear()
      : null;
    const isBuildingOlderThan100Years =
      constructionYear && constructionYear < currentYear - 100;

    const isSurfaceAreaLessThan500 = formValues?.surface_area < 500;

    const postalCodesToCheck = [
      '13000',
      '13001',
      '13002',
      '13003',
      '13004',
      '13005',
      '13006',
      '13007',
      '13008',
      '13009',
      '13010',
      '13011',
      '13012',
      '13013',
      '13014',
      '13015',
      '13016',
      '59000',
      '59010',
      '59011',
      '59012',
      '59013',
      '59014',
      '59015',
      '59016',
      '59017',
      '59018',
      '59800',
      '59130',
      '59160',
      '58350',
      '50977',
      '31000',
      '31001',
      '31002',
      '31003',
      '31004',
      '31005',
      '31006',
      '31007',
      '31008',
      '31009',
      '33000',
      '33010',
      '33011',
      '33012',
      '33013',
      '33014',
      '33015',
      '33016',
      '33017',
      '33018'
    ];

    const buildingPostalCode = formValues?.buildings?.[0]?.address?.postal_code;
    const isPostalCodeMatched = postalCodesToCheck.includes(buildingPostalCode);

    const shouldDisplayCollapse = !(
      isBuildingOlderThan100Years &&
      isSurfaceAreaLessThan500 &&
      isPostalCodeMatched
    );

    setDisplayCollapse(shouldDisplayCollapse);
    if (!shouldDisplayCollapse) {
      setFormValues((prevValues) => ({
        ...prevValues,
        g0230_collapse: false
      }));
    }
  }, [
    formValues?.surface_area,
    formValues?.construction_year,
    formValues?.buildings?.length
  ]);

  const getLCI = () => {
    const surface = formValues?.surface_area;
    const isPlus50 = formValues?.occupation_trade === 'plus_50';
    const isEmptyUnoccupied = formValues?.empty_unoccupied_works;

    let lci = 1000000;

    if (isPlus50) {
      if (surface <= 500) {
        lci = 1000000;
      } else if (surface <= 1200) {
        lci = 3000000;
      } else {
        lci = isEmptyUnoccupied ? 3000000 : 5900000;
      }
    } else if (surface <= 300) {
      lci = 1000000;
    } else if (surface <= 600) {
      lci = 3000000;
    } else {
      lci = isEmptyUnoccupied ? 3000000 : 5900000;
    }

    setFormValues({
      ...formValues,
      lci
    });

    return lci;
  };

  const sitesKey = useMemo(
    () => JSON.stringify(formValues?.sites),
    [formValues?.sites]
  );

  const lciValue = useMemo(
    () => getLCI(),
    [
      sitesKey,
      formValues?.turnover,
      formValues?.rc_pro_guarantee,
      formValues?.furniture_capital,
      formValues?.surface_area,
      formValues?.occupation_trade,
      formValues?.empty_unoccupied_works
    ]
  );

  const openNotificationWithIcon = (
    notificationType,
    value,
    descriptionKey
  ) => {
    if (descriptionKey) {
      notification[notificationType]({
        message: t('contracts.form.warning_title'),
        description: t(descriptionKey),
        duration: 7,
        key: 'oneTime'
      });
    } else if (value) {
      notification[notificationType]({
        message: 'Notification de dérogation',
        description: t('help.activities'),
        duration: 7,
        key: 'oneTime'
      });
    } else {
      notification[notificationType]({
        message: t('contracts.form.contribution_adjustment_message'),
        key: 'oneTime'
      });
    }
  };

  useEffect(() => {
    if (exemptions.includes(checkedVal)) {
      openNotificationWithIcon('warning', 'value');
    } // eslint-disable-next-line
  }, [exemptions.length]);

  useEffect(() => {
    setFormValues({ ...formValues, furniture_capital: undefined });
    // eslint-disable-next-line
  }, [form.getFieldValue('risk_typology')]);

  useEffect(() => {
    if (formValues?.volunteer_trustee) {
      setFormValues({
        ...formValues,
        rc_volunteer_union: true
      });
    }
    if (!formValues?.volunteer_trustee) {
      setFormValues({
        ...formValues,
        rc_volunteer_union: false
      });
    }
  }, [formValues?.volunteer_trustee]);

  useEffect(() => {
    if (number_activity) {
      const activities_trade = [];
      for (let i = 0; i < formValues.number_activity; i += 1) {
        activities_trade[i] = form.getFieldValue('activities_trade')?.[i] || '';
      }
      form.setFieldsValue({ activities_trade });
    } else form.setFieldsValue({ activities_trade: [] });
    // eslint-disable-next-line
  }, [number_activity, estimatedCost]);

  useEffect(() => {
    if (estimatedCost && estimatedCost.cat_nat_risk) {
      form.setFieldsValue({ cat_nat_risk: estimatedCost.cat_nat_risk });
      setFormValues({
        ...formValues,
        cat_nat_risk: estimatedCost.cat_nat_risk
      });
    }
    // eslint-disable-next-line
  }, [catNat]);

  const getCommuneCode = (value, key) => {
    try {
      setIsLoading(true);
      if (value) {
        const communCode = cities.find(
          (city) => city.nom === value.split(' (')[0]
        ).code;
        form.setFieldsValue({
          buildings: form.getFieldValue('buildings').map((values, index) =>
            index === key
              ? {
                  ...values,
                  address: {
                    ...values.address,
                    commune_code: communCode
                  }
                }
              : { ...values }
          )
        });
        setFormValues({
          ...formValues,
          buildings: form.getFieldValue('buildings').map((values, index) =>
            index === key
              ? {
                  ...values,
                  address: {
                    ...values.address,
                    commune_code: communCode
                  }
                }
              : { ...values }
          )
        });
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const setAddress = (key, data, commune_code) =>
    form.getFieldValue('buildings').map((values, index) =>
      index === key
        ? {
            ...values,
            address: {
              number: data.result.address_components[0].long_name,
              street: data.result.address_components[1].long_name,
              administrative_area_level_2:
                data.result.address_components[3].long_name,
              postal_code: data.result.address_components[6].long_name,
              city: data.result.address_components[2].long_name,
              formatted_address: data.result.formatted_address,
              lat: data.result.geometry.location.lat,
              lng: data.result.geometry.location.lng,
              commune_code,
              address_autocomplete: ''
            }
          }
        : { ...values }
    );

  const getCity = async (postal_code, placeIdData, key) => {
    setIsCityLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/${postal_code}`
      });
      const tmpData = data
        .map((city) => {
          if (city.codesPostaux.length > 1) {
            return city.codesPostaux.map((codePostal) => ({
              nom: city.nom.toUpperCase(),
              codePostal,
              code: city.code
            }));
          }
          return {
            nom: city.nom.toUpperCase(),
            codePostal: city.codesPostaux[0],
            code: city.code
          };
        })
        .flat();
      setCities(tmpData);
      setIsCityLoading(false);
      setErrorGetCities(false);
      let commune_code = '';
      if (placeIdData) {
        if (tmpData) {
          commune_code = tmpData.find(
            (city) =>
              placeIdData.result.address_components[2].long_name.toUpperCase() ===
              city.nom
          ).code;
        }
        form.setFieldsValue({
          buildings: setAddress(key, placeIdData, commune_code)
        });
        setFormValues({
          ...formValues,
          buildings: setAddress(key, placeIdData, commune_code)
        });
      }
      return tmpData;
    } catch (e) {
      if (e.response) {
        message(e.response.status);
      }
      setIsCityLoading(false);
      const isNotFoundError = e.response?.status === 404;
      setErrorGetCities(!isNotFoundError);
      return [];
    }
  };

  const handleMainUseChange = (value) => {
    const updatedFormValues = {
      ...formValues,
      main_use: value?.target?.value
    };
    form.setFieldsValue({
      main_use: value?.target?.value
    });
    if (value?.target?.value !== 'rec_TRADE') {
      updatedFormValues.occupation_trade = 'under_25';
      form.setFieldsValue({
        occupation_trade: 'under_25'
      });
    }
    setFormValues({ ...updatedFormValues });
  };

  const handlePostalCodeChange = async (event, key) => {
    const { value } = event.target;

    const newBuildingsArr = form
      .getFieldValue('buildings')
      .map((values, index) =>
        index === key
          ? {
              ...values,
              address: {
                ...values.address,
                city: undefined
              }
            }
          : { ...values }
      );

    form.setFieldValue(['buildings', key, 'address', 'city'], undefined);
    setFormValues({
      ...formValues,
      buildings: newBuildingsArr
    });

    if (isValidZipcode(value)) {
      setPostalCode(value);
      const tmpCities = await getCity(value.slice(0, 2));

      if (tmpCities) {
        const filteredCities = tmpCities.filter((city) => {
          let isMatching = false;

          if (city.codePostal.startsWith(value)) {
            isMatching = true;
          }

          return isMatching;
        });

        const newBuildingsArr = form
          .getFieldValue('buildings')
          .map((values, index) =>
            index === key
              ? {
                  ...values,
                  address: {
                    ...values.address,
                    city: `${filteredCities[0].nom} (${filteredCities[0].codePostal})`
                  }
                }
              : { ...values }
          );

        form.setFieldValue(
          ['buildings', key, 'address', 'city'],
          `${filteredCities[0].nom} (${filteredCities[0].codePostal})`
        );
        setFormValues({
          ...formValues,
          buildings: newBuildingsArr
        });
      }
    }
  };

  const getAddressAutocomplete = async (address_autocomplete) => {
    if (address_autocomplete) {
      setIsLoading(true);
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/${address_autocomplete}`
        });
        setAddressSuggestion(data?.predictions);
      } catch (e) {
        if (e.response) message(e.response.status);
      }
      setIsLoading(false);
    }
  };

  const getAddressPlaceId = async (placeId, key) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/places/placeid/${placeId}`
      });
      if (data) {
        const postalCodeFind = data.result.address_components
          .find((component) => component.types.includes('postal_code'))
          ?.long_name.substring(0, 2);
        if (postalCodeFind) {
          await getCity(postalCodeFind, data, key);
        }
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const disabledDate = (current) => {
    return current && current > moment().endOf('day');
  };
  const mrimlFields = [
    {
      name: ['buildings'],
      noLabel: true,
      noStyle: true,
      tooltip: true,
      input: (
        <Form.List name="buildings" initialValue={[{}]}>
          {(fields, { remove }) => (
            <>
              {fields.map((field) => {
                return (
                  <div className="mri-building-address-wrapper">
                    <Form.Item
                      {...field}
                      key={[field.name, 'address', 'address_autocomplete']}
                      name={[field.name, 'address', 'address_autocomplete']}
                      label={t('contracts.form.address.address_autocomplete')}
                      hasFeedback={isLoading}
                      {...(isLoading ? { validateStatus: 'validating' } : {})}
                    >
                      <Select
                        disabled={field.name !== 0}
                        filterOption={false}
                        showSearch
                        showArrow={false}
                        notFoundContent={null}
                        onSearch={getAddressAutocomplete}
                        onSelect={async (selected) => {
                          const placeId = selected.split('#')[0];
                          await getAddressPlaceId(placeId, field.name);
                        }}
                        options={
                          addressSuggestion &&
                          addressSuggestion.map((predictions) => ({
                            label: predictions.description,
                            value: `${predictions.place_id}#${predictions.description}`
                          }))
                        }
                        autoComplete="anyrandomstring"
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'number']}
                      key={[field.name, 'address', 'number']}
                      label={t('contracts.form.address.number')}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    >
                      <InputNumber />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'street']}
                      key={[field.name, 'address', 'street']}
                      label={t('contracts.form.address.street')}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'additional']}
                      key={[field.name, 'address', 'additional']}
                      label={t('contracts.form.address.additional')}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'postal_code']}
                      key={[field.name, 'address', 'postal_code']}
                      label={t('contracts.form.address.postal_code')}
                      help={
                        exemptions.includes('administrative_area_level_2')
                          ? t('help.exemptions')
                          : null
                      }
                      rules={[
                        {
                          required: true,
                          message: t('errors.form.REQUIRED')
                        },
                        {
                          validator(_, value) {
                            if (!isValidZipcode(value)) {
                              return Promise.reject(
                                new Error(
                                  'Le code postal doit être composé de 5 chiffres'
                                )
                              );
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input
                        style={{ width: '90px' }}
                        onChange={(event) =>
                          handlePostalCodeChange(event, field.name)
                        }
                        autoComplete="off"
                      />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'commune_code']}
                      key={[field.name, 'address', 'commune_code']}
                      label={t('contracts.form.commune_code')}
                      hidden
                    >
                      <Input disabled style={{ width: '90px' }} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'city']}
                      key={[field.name, 'address', 'city']}
                      label={t('contracts.form.address.city')}
                      hasFeedback={isCityLoading}
                      {...(isCityLoading
                        ? { validateStatus: 'validating' }
                        : {})}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    >
                      {!errorGetCities ? (
                        <Select
                          showSearch
                          onSelect={(selected) => {
                            getCommuneCode(selected, field.name);
                          }}
                          options={
                            cities &&
                            cities
                              .filter((city) => {
                                let isMatching = false;

                                if (city.codePostal.startsWith(postalCode)) {
                                  isMatching = true;
                                }

                                return isMatching;
                              })
                              .map((city) => ({
                                label: `${city.nom} (${city.codePostal})`,
                                value: `${city.nom} (${city.codePostal})`
                              }))
                          }
                        />
                      ) : (
                        <Input />
                      )}
                    </Form.Item>
                    {formValues &&
                      formValues?.buildings &&
                      formValues?.buildings[0]?.address && (
                        <>
                          <a
                            className="form-map-link mri-map-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${
                              formValues?.buildings[0].address &&
                              formValues?.buildings[0].address.lat
                            }0%2C${
                              formValues?.buildings[0].address &&
                              formValues?.buildings[0].address.lng
                            }&heading=-45&pitch=38&fov=80`}
                          >
                            {`Visualiser l'immeuble `}
                            <img src={GoogleMapsIcon} alt="icone Google Maps" />
                          </a>
                        </>
                      )}
                    <Form.Item
                      {...field}
                      name={[field.name, 'building_name']}
                      key={[field.name, 'building_name']}
                      label={t('contracts.form.building_name')}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'building_siret']}
                      key={[field.name, 'building_siret']}
                      label={t('contracts.form.building_siret')}
                    >
                      <Input min={0} minLength={9} maxLength={14} />
                    </Form.Item>
                    {formValues?.buildings?.length &&
                      formValues?.buildings?.length > 1 &&
                      field.name !== 0 && (
                        <>
                          <Form.Item>
                            <Button
                              style={{ color: 'purple' }}
                              type="link"
                              danger
                              onClick={() => remove(field.name)}
                            >
                              {`${t('buttons.removeBuilding')} ${
                                field.name + 1
                              }`}
                            </Button>
                          </Form.Item>
                        </>
                      )}
                  </div>
                );
              })}
            </>
          )}
        </Form.List>
      ),
      step: 0
    },
    {
      name: ['total_buildings'],
      rules: [{ required: true }],
      tooltip: true,
      tooltipName: 'total_buildings_ml',
      // help: exemptions.includes('total_buildings') TMP
      //   ? t('help.exemptions')
      //   : null,
      input: <InputNumber min={0} />,
      step: 0
    },
    {
      name: ['main_use'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <>
          <Form.Item name="main_use" noStyle>
            <Radio.Group
              buttonStyle="solid"
              className="radio-with-icon radio-with-icon-text-wrap radio-with-icon-row-wrap"
              onChange={(value) => handleMainUseChange(value)}
            >
              <Radio.Button
                value="rec_DWELLING"
                className="radio-with-icon-long-text"
              >
                <HouseIcon />
                <span style={{ fontSize: 8 }}>
                  {t('contracts.tags.rec_DWELLING')}
                </span>
              </Radio.Button>
              <Radio.Button
                value="rec_OFFICE"
                className="radio-with-icon-long-text"
              >
                <OfficeIcon />
                <span style={{ fontSize: 8 }}>
                  {t('contracts.tags.rec_OFFICE_WITH_SHOP')}
                </span>
              </Radio.Button>
              <Radio.Button
                value="rec_TRADE"
                className="radio-with-icon-long-text"
              >
                <CommercialIcon />
                <span style={{ fontSize: 10 }}>
                  {t('contracts.tags.rec_TRADE_PRO')}
                </span>
                {t('contracts.tags.rec_TRADE')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </>
      ),
      help: exemptions.includes('main_use') ? t('help.exemptions') : null,
      step: 0
    },
    ...(formValues && formValues?.main_use === 'rec_TRADE'
      ? [
          {
            name: ['occupation_trade'],
            label: ['occupation_trade_ml'],
            rules: [{ required: true }],
            input: (
              <Form.Item name="occupation_trade" noStyle>
                <Radio.Group buttonStyle="solid">
                  <Radio.Button value="between_25_50">
                    {t('contracts.tags.between_25_50')}
                  </Radio.Button>
                  <Radio.Button value="plus_50">
                    {t('contracts.tags.plus_50')}
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            ),
            help: exemptions.includes('occupation_trade')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    {
      name: ['risk_typology'],
      rules: [{ required: true }],
      noStyle: true,
      tooltip: true,
      input: (
        <Form.Item name="risk_typology" noStyle>
          <Radio.Group buttonStyle="solid" className="radio-with-icon">
            <Radio.Button value="STANDARD">
              <StandardIcon />
              {t('contracts.tags.STANDARD')}
            </Radio.Button>
            <Radio.Button value="LUXE">
              <StandingIcon />
              {t('contracts.tags.LUXE')}
            </Radio.Button>
            <Radio.Button value="METAL_FRAME">
              <DilapidatedIcon />
              {t('contracts.tags.METAL_FRAME')}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      ),
      help: exemptions.includes('risk_typology') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['construction_year'],
      tooltip: true,
      rules: [{ required: true }],
      input: (
        <DatePicker
          format={dateFormatYr}
          picker="year"
          style={{ minWidth: 200 }}
          disabledDate={disabledDate}
        />
      ),
      step: 0
    },
    {
      name: ['acquisition_date'],
      tooltip: true,
      rules: [{ required: true }],
      input: (
        <DatePicker
          format={dateFormat}
          picker="date"
          style={{ minWidth: 200 }}
        />
      ),
      step: 0
    },
    ...(isAcquisitionDateMoreThan2MonthsOld
      ? [
          {
            name: ['is_continuously_insured_since_acquisition'],
            input: <PreRequisitesSwitch />,
            help: exemptions.includes(
              'is_continuously_insured_since_acquisition'
            )
              ? t('help.exemptions')
              : null,
            valuePropName: 'checked',
            step: 0
          }
        ]
      : []),
    {
      name: ['surface_area'],
      label: ['surface_mri_ml'],
      tooltip: true,
      tooltipName: 'surface_mri_ml',
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: <InputNumber min={0} />,
      help: exemptions.includes('surface_area') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['building_situation'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <Radio.Group
          buttonStyle="solid"
          className="radio-with-icon-text-wrap radio-with-icon"
        >
          <Radio.Button value="CO_OWNERSHIP">
            <EntityIcon />
            Copropriété
          </Radio.Button>
          <Radio.Button value="SINGLE_PROPERTY">
            <PersonIcon />
            Mono-propriété
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['absence_concrete_floor'],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0
    },
    {
      name: ['volunteer_trustee'],
      tooltip: true,
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0
    },
    {
      name: ['indoor_parking'],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0
    },
    {
      name: ['empty_unoccupied_works'],
      label: 'empty_unoccupied_works_mila',
      tooltip: false,
      input: (
        <PreRequisitesSwitch
          onChange={(value) => {
            form.setFieldsValue({
              deductible_cost_index: undefined,
              guarantee_type: undefined
            });
            if (value) {
              notification.info({
                message: t('contracts.messages.info_empty_unoccupied_works'),
                duration: 10
              });
            }
          }}
        />
      ),
      help: exemptions.includes('empty_unoccupied_works')
        ? t('help.exemptions')
        : null,
      valuePropName: 'checked',
      step: 0
    },
    ...(formValues && formValues.empty_unoccupied_works
      ? [
          {
            name: ['empty_reason'],
            rules: [{ required: true }],
            input: (
              <>
                <Form.Item name="empty_reason" noStyle>
                  <Radio.Group buttonStyle="solid" className="radio-with-icon">
                    <Radio.Button
                      value="empty_innocupied"
                      style={{ width: 130 }}
                    >
                      <UnoccupiedIcon />
                      {t('contracts.tags.empty_innocupied')}
                    </Radio.Button>
                    <Radio.Button
                      value="under_construction_minus4"
                      style={{ width: 130 }}
                    >
                      <ConstructionIcon />
                      {t('contracts.tags.under_construction_minus4')}
                    </Radio.Button>
                    <Radio.Button
                      value="under_construction_plus4"
                      style={{ width: 130 }}
                    >
                      <ConstructionLongIcon />
                      {t('contracts.tags.under_construction_plus4')}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </>
            ),
            step: 0
          }
        ]
      : []),
    ...(formValues && formValues.empty_unoccupied_works
      ? [
          {
            name: ['empty_space'],
            rules: [{ required: true }],
            input: (
              <>
                <Form.Item name="empty_space" noStyle>
                  <Radio.Group
                    buttonStyle="solid"
                    onChange={(e) => {
                      if (e.target.value !== 'empty_space025') {
                        const restrictedIndices = [
                          '0x',
                          '0,25x',
                          '0,5x',
                          '0,75x',
                          '1x',
                          '1,5x'
                        ];
                        const currentIndex = form.getFieldValue(
                          'deductible_cost_index'
                        );
                        const currentType =
                          form.getFieldValue('guarantee_type');
                        const updates = {};
                        let needsWarning = false;
                        if (restrictedIndices.includes(currentIndex)) {
                          updates.deductible_cost_index = '2x';
                          needsWarning = true;
                        }
                        if (currentType === 'TYPE3') {
                          updates.guarantee_type = 'TYPE2';
                          needsWarning = true;
                        }
                        if (needsWarning) {
                          form.setFieldsValue(updates);
                          openNotificationWithIcon(
                            'warning',
                            null,
                            'contracts.form.empty_space_restriction_warning'
                          );
                        }
                      }
                    }}
                  >
                    <Radio.Button value="empty_space100">
                      {t('contracts.tags.empty_space100')}
                    </Radio.Button>
                    <Radio.Button value="empty_space075">
                      {t('contracts.tags.empty_space075')}
                    </Radio.Button>
                    <Radio.Button value="empty_space050">
                      {t('contracts.tags.empty_space050')}
                    </Radio.Button>
                    <Radio.Button value="empty_space025">
                      {t('contracts.tags.empty_space025')}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </>
            ),
            step: 0
          }
        ]
      : []),
    {
      name: ['specific_risk'],
      rules: [{ required: true }],
      help: exemptions.includes('specific_risk') ? t('help.exemptions') : null,
      input: (
        <Form.Item name="specific_risk" noStyle>
          <Radio.Group
            buttonStyle="solid"
            className="radio-with-icon radio-with-icon-text-wrap radio-with-icon-row-wrap"
          >
            <Radio.Button value="NIGHTCLUB">
              <OtherBuildingIcon />
              {t('contracts.tags.NIGHTCLUB')}
            </Radio.Button>
            <Radio.Button value="HOSTEL">
              <HotelIcon />
              {t('contracts.tags.HOSTEL')}
            </Radio.Button>
            <Radio.Button value="AUTO_TRADER">
              <OtherBuildingIcon />
              {t('contracts.tags.AUTO_TRADER')}
            </Radio.Button>
            <Radio.Button value="CONSULATE">
              <PoliticalOfficeIcon />
              {t('contracts.tags.CONSULATE')}
            </Radio.Button>
            <Radio.Button value="WORSHIP">
              <PlaceWorshipIcon />
              {t('contracts.tags.WORSHIP')}
            </Radio.Button>
            <Radio.Button value="POLITICAL_OFFICE">
              <PoliticalOfficeIcon />
              {t('contracts.tags.POLITICAL_OFFICE')}
            </Radio.Button>
            <Radio.Button value="UNION">
              <HouseIcon />
              {t('contracts.tags.UNION')}
            </Radio.Button>
            <Radio.Button value="EMBASSY">
              <PoliticalOfficeIcon />
              {t('contracts.tags.EMBASSY')}
            </Radio.Button>
            <Radio.Button value="NONE">
              <StandardIcon />
              {t('contracts.tags.NONE')}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      ),
      step: 0
    },
    {
      name: ['risk_been_insured'],
      label: 'building_been_insured_last_36_months_no_gap',
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0
    },
    ...(formValues && formValues.risk_been_insured
      ? [
          {
            name: ['last_company_name'],
            input: <Input style={{ width: 500 }} />,
            rules: [{ required: true }],
            step: 0
          },
          {
            name: ['last_contract_number'],
            input: <Input style={{ width: 500 }} />,
            step: 0
          },
          {
            name: ['last_termination_date'],
            input: <DatePicker format={dateFormat} style={{ minWidth: 200 }} />,
            step: 0
          }
        ]
      : []),
    ...(formValues &&
    formValues.risk_been_insured &&
    formValues.surface_area >= 2000
      ? [
          {
            name: ['information_statements_submission_date'],
            tooltip: true,
            input: <PreRequisitesSwitch />,
            valuePropName: 'checked',
            step: 0
          }
        ]
      : []),
    {
      name: ['number_claims'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <InputNumber
          min={0}
          onChange={(number_claims) => {
            let deductibleIndex = formValues.deductible_cost_index;
            if (
              number_claims > 0 &&
              ['0x', '0,25x', '0,5x'].includes(formValues.deductible_cost_index)
            ) {
              form.setFieldsValue({
                deductible_cost_index: '0,75x'
              });
              deductibleIndex = '0,75x';
            }
            setFormValues((prevState) => ({
              ...prevState,
              deductible_cost_index: deductibleIndex,
              number_claims
            }));
          }}
        />
      ),
      help: exemptions.includes('number_claims') ? t('help.exemptions') : null,
      step: 0
    },
    ...(formValues && formValues.number_claims >= 1
      ? [
          {
            name: ['claims'],
            noLabel: false,
            step: 0,
            help: exemptions.includes(`claim_amount`)
              ? t('help.exemptions')
              : null,
            input: (
              <Form.List name="claims">
                {(fields) => (
                  <div>
                    {fields.map((field) => (
                      <Space
                        key={field.key}
                        style={{ display: 'flex', marginBottom: 8 }}
                        align="baseline"
                      >
                        <Form.Item
                          noStyle
                          {...field}
                          name={[field.name, 'claim_date']}
                          key={[field.fieldKey, 'claim_date']}
                          rules={[
                            {
                              required: true,
                              message: 'Date du sinistre manquant'
                            }
                          ]}
                        >
                          <DatePicker
                            format={dateFormat}
                            style={{ minWidth: 200 }}
                            disabledDate={(current) => {
                              return current >= moment();
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          {...field}
                          name={[field.name, 'claim_type']}
                          key={[field.fieldKey, 'claim_type']}
                          rules={[
                            {
                              required: true,
                              message: 'Type du sinistre manquant'
                            }
                          ]}
                        >
                          <Input
                            style={{ width: '200px' }}
                            placeholder="Type de sinistre"
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          {...field}
                          name={[field.name, 'claim_amount']}
                          key={[field.fieldKey, 'claim_amount']}
                          rules={[
                            {
                              required: true,
                              message: 'Montant du sinistre manquant'
                            }
                          ]}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '200px' }}
                            formatter={(value) => value && `${value}€`}
                            parser={(value) => value.replace('€', '')}
                            placeholder="Montant du sinistre"
                          />
                        </Form.Item>
                      </Space>
                    ))}
                  </div>
                )}
              </Form.List>
            )
          }
        ]
      : []),
    {
      name: ['termination_by_company'],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0,
      tooltip: true
    },
    ...(formValues && formValues.termination_by_company
      ? [
          {
            name: ['termination_reason_by_company'],
            rules: [{ required: true }],
            input: (
              <Select style={{ width: 220 }}>
                {enums?.termination_reason_by_company &&
                  enums.termination_reason_by_company.map((e) => (
                    <Option value={e} key={e}>
                      {t(`contracts.form.${e}`)}
                      <Tooltip
                        title={t(`contracts.form.tooltip.${e}`)}
                        overlayInnerStyle={{ width: 500 }}
                      >
                        <QuestionCircleOutlined style={{ float: 'right' }} />
                      </Tooltip>
                    </Option>
                  ))}
              </Select>
            ),
            help: exemptions.includes('termination_by_company')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    ...(formValues &&
    formValues.termination_reason_by_company === 'termination_other'
      ? [
          {
            name: ['termination_reason_by_company_custom'],
            rules: [{ required: true }],
            step: 0
          }
        ]
      : []),
    ...(formValues && formValues.termination_by_company
      ? [
          {
            name: ['termination_date_by_company'],
            input: <DatePicker format={dateFormat} style={{ minWidth: 200 }} />,
            step: 0
          }
        ]
      : []),
    {
      name: ['deductible_cost_index'],
      tooltip: !!(formValues && formValues.empty_unoccupied_works),
      rules: [{ required: true }],
      input: (
        <Select>
          {enums?.costIndexes &&
            formValues &&
            enums.costIndexes
              .filter(
                (e, index) =>
                  (!formValues.empty_unoccupied_works ||
                    (formValues.empty_unoccupied_works &&
                      formValues.empty_space === 'empty_space025') ||
                    index >= 6) &&
                  (!formValues.number_claims ||
                    formValues.number_claims === 0 ||
                    !['0x', '0,25x', '0,5x'].includes(e))
              )
              .map(
                (e) =>
                  e !== '45x' && (
                    <Option value={e} key={e}>
                      {t(`contracts.tags.${e}`)}
                    </Option>
                  )
              )}
        </Select>
      ),
      help: exemptions.includes('deductible_cost_index')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    {
      step: 0,
      name: ['comparisonMRI'],
      noStyle: true,
      noLabel: true,
      layout: {
        labelCol: { span: 0 },
        wrapperCol: { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }
      },
      input: (
        <ComparisonTableMRI
          form={form}
          type="mriml"
          fields={[
            { name: ['g1231_property_owner_civil_liability'] }, // f1
            { name: ['g1231_criminal_defense_recourse_following_accident'] }, // f1
            { name: ['g0230_climatic_events'] }, // f2 new
            { name: ['g1231_attacks'] },
            { name: ['g0230_motorized_vehicle_impact'] }, // new
            { name: ['g1231_natural_disasters'] },
            { name: ['g1231_technological_risks'] },
            { name: ['g1231_fire_related_risks'] },
            { name: ['g0030_water_damage'] }, // f3
            { name: ['g0230_theft_vandalism'] },
            { name: ['g0030_broken_glass'] },
            { name: ['g0230_electrical_damage'] },
            { name: ['g0030_broken_machine'] },
            ...(displayCollapse ? [{ name: ['g0230_collapse'] }] : [])
          ]}
          columns={
            formValues?.empty_space === 'empty_space100'
              ? [{ title: 'TYPE1' }]
              : [{ title: 'TYPE1' }, { title: 'TYPE2' }, { title: 'TYPE3' }]
          }
        />
      )
    },
    {
      name: ['legal_protection'],
      label: 'legal_protection_option',
      tooltipName: 'legal_protection_pnoml',
      input: (
        <Switch
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
        />
      ),
      step: 0
    },
    {
      name: ['selected_options'],
      input: (
        <Checkbox.Group className="checkbox-options-mri">
          <Checkbox value="outdoor_indoor_pool">
            <OutdoorPoolIcon />
            {t('contracts.form.outdoor_indoor_pool')}
          </Checkbox>
          <Checkbox value="renewable_energies">
            <SolarPanelIcon />
            {t('contracts.form.renewable_energies')}
          </Checkbox>
        </Checkbox.Group>
      ),
      step: 0
    },
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          <Radio.Button value="MONTHLY">
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['is_lci_of_x_million_sufficient'],
      nameParams: lciValue,
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_assured_location_outside_metropolitan_france'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['activity_in_qpv_area'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_under_construction_or_90_percent_materials'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_for_depollution_rehabilitation'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['fire_claim_last_60_months_subscriber_affiliates'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_includes_veranda_over_3m2'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_wooden_structure_or_wood_cladding'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_contiguous_with_industrial_activity'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['proximity_of_building_to_risk_of_peril_in_marseille'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_occupied_illegally'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_subject_to_alignment'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_contains_inflammable_liquids'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_above_1500m_and_inaccessible_by_rescue'],
      valuePropName: 'checked',
      step: 1
    },
    ...(formValues && formValues?.building_situation === 'SINGLE_PROPERTY'
      ? [
          {
            name: ['presence_of_lease_with_waiver_of_recourse_clause'],
            valuePropName: 'checked',
            step: 1
          }
        ]
      : []),
    {
      name: ['building_subject_to_major_works_or_renovation'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_dedicated_to_demolition'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['establishments_receiving_public_category_1_to_4'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['establishments_receiving_public_category_5_closing_before_23h'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_involves_specific_high_risk_activities'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['roof_non_standard_materials'],
      valuePropName: 'checked',
      help: exemptions.includes('roof_non_standard_materials')
        ? t('help.exemptions')
        : null,
      step: 1
    },
    {
      name: ['risk_under_judicial_administration'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['policyholder_in_bankruptcy_or_liquidation'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_vacant_land'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_declared_contains_high_risk_activities_or_premises'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_contains_religious_political_diplomatic_or_union_activity'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_declared_is_high_rise_or_large_real_estate_complex'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_declared_is_a_castle'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_declared_is_classified_or_listed_building'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_an_unbuildable_land'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_contains_cold_storage_above_120m3'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_declared_is_in_flood_zone'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_is_not_in_good_condition'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_declared_under_unsanitary_or_dangerous_decree'],
      valuePropName: 'checked',
      step: 1
    },
    ...(formValues &&
    (formValues?.risk_is_not_in_good_condition ||
      formValues?.empty_unoccupied_works)
      ? [
          {
            name: ['will_property_be_renovated_in_4_months'],
            valuePropName: 'checked',
            step: 1
          }
        ]
      : []),
    ...(formValues && formValues?.empty_unoccupied_works
      ? [
          {
            name: ['will_property_be_renovated_in_more_than_4_months'],
            valuePropName: 'checked',
            step: 1
          }
        ]
      : []),
    {
      name: ['is_property_dealer'],
      valuePropName: 'checked',
      step: 1
    }
  ];

  return {
    mrimlFields: mrimlFields.map((f) => {
      if (f.step === 1)
        return {
          ...f,
          input: (
            <PreRequisitesSwitch
              onChange={(val) => {
                setFormValues({
                  ...formValues,
                  [f.name[0]]: val
                });
                setCheckedVal(f.name[0]);
              }}
            />
          ),
          valuePropName: 'checked',
          labelAlign: 'left',
          withDivider: true,
          layout: preRequisitesLayout
        };
      return f;
    })
  };
};

export default useMRIMLFields;
